/**
 * Analytics Utility Functions
 * Handles initialization and consistent tracking for Google Analytics
 */

// Safely access gtag if available
export const gtag = function() {
  if (window.gtag) {
    window.gtag.apply(null, arguments);
  } else {
    console.warn('Google Analytics not loaded yet');
  }
};

/**
 * Track page views consistently across the application
 * @param {string} path - The page path to track
 * @param {string} title - The page title
 */
export const trackPageView = (path, title) => {
  try {
    // Use URL constructor to handle any special characters correctly
    const pageUrl = new URL(path, window.location.origin);
    
    gtag('event', 'page_view', {
      page_title: title || document.title,
      page_location: pageUrl.toString(),
      page_path: pageUrl.pathname,
      send_to: 'G-NEQ1RHQ06X'
    });
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

/**
 * Track events consistently
 * @param {string} eventName - Name of the event
 * @param {Object} params - Event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  try {
    gtag('event', eventName, {
      ...params,
      send_to: 'G-NEQ1RHQ06X',
      event_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

/**
 * Initialize analytics with specific error handling for privacy-sandbox issues
 */
export const initAnalytics = () => {
  // Fix for Invalid Header Value errors in privacy-sandbox
  if (window.navigator && window.navigator.registerProtocolHandler) {
    try {
      // Catch and handle errors from registerProtocolHandler
      const originalSendBeacon = navigator.sendBeacon;
      if (originalSendBeacon) {
        navigator.sendBeacon = function(url, data) {
          try {
            // Fix malformed URLs that might cause header issues
            if (url.includes('privacy-sandbox')) {
              const sanitizedUrl = url.split('?')[0]; // Remove query params that might cause issues
              return originalSendBeacon.call(this, sanitizedUrl, data);
            }
            return originalSendBeacon.apply(this, arguments);
          } catch (e) {
            console.warn('Beacon error prevented:', e);
            return false;
          }
        };
      }
    } catch (e) {
      console.warn('Error setting up analytics error handling:', e);
    }
  }

  // Patch XMLHttpRequest to catch and handle errors related to analytics
  const originalXhrOpen = window.XMLHttpRequest.prototype.open;
  window.XMLHttpRequest.prototype.open = function() {
    try {
      const url = arguments[1];
      if (typeof url === 'string' && 
          (url.includes('google-analytics') || url.includes('privacy-sandbox'))) {
        
        // Add error handling for analytics requests
        this.addEventListener('error', function(e) {
          console.warn('Prevented analytics error:', e);
          // Prevent the error from bubbling up
          e.stopPropagation();
        });
      }
    } catch (e) {
      // Fail silently - don't break application functionality
    }
    
    return originalXhrOpen.apply(this, arguments);
  };
};

// Fix ESLint warning by assigning the object to a variable before exporting
const analyticsModule = {
  initAnalytics,
  trackPageView,
  trackEvent,
  gtag
};

export default analyticsModule; 